/**
 * WHAT: Post-attestation report showing attestation decisions and implementation status for completed campaigns.
 * WHY:  After attestation is complete, compliance teams need to see what decisions were made (Retained,
 *       Modify, Removed) and whether those decisions have been implemented, providing the closing
 *       evidence for each attestation cycle.
 */
import React, { useState, useEffect } from 'react';
import API_CONFIG from '../config/api.config';
import { TopologySection } from './common';
import { formatDateTime, formatDateTimeForReport } from '../utils/dateUtils';

/**
 * WHAT: Renders post-attestation results for a selected completed campaign with decision and implementation filters.
 * WHY:  Enables compliance officers to verify that attestation outcomes have been acted upon,
 *       track implementation gaps, and export the final attestation record as CSV for audit evidence.
 */
const PostAttestationReport = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDecision, setFilterDecision] = useState('all');
  const [filterImplementation, setFilterImplementation] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50);

  useEffect(() => {
    loadCampaigns();
  }, []);

  const loadCampaigns = async () => {
    setIsLoadingCampaigns(true);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('campaigns/'));
      const data = await response.json();

      if (data.success) {
        // Filter campaigns that are COMPLETED (post-attestation requires completed campaigns)
        const eligibleCampaigns = data.campaigns.filter(
          campaign => campaign.status === 'COMPLETED'
        );
        setCampaigns(eligibleCampaigns);
      } else {
        setError('Failed to load campaigns');
      }
    } catch (err) {
      setError('Error loading campaigns: ' + err.message);
    } finally {
      setIsLoadingCampaigns(false);
    }
  };

  const loadReport = async () => {
    if (!selectedCampaignId) {
      setError('Please select a campaign');
      return;
    }

    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      const response = await fetch(
        API_CONFIG.getDatabasesEndpoint(`reports/post-attestation/?campaign_id=${selectedCampaignId}`)
      );
      const data = await response.json();

      if (data.success) {
        setReportData(data);
        setCurrentPage(1);
      } else {
        setError(data.error || 'Failed to load report');
      }
    } catch (err) {
      setError('Error loading report: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = async () => {
    if (!selectedCampaignId) {
      setError('Please select a campaign first');
      return;
    }

    try {
      window.open(
        API_CONFIG.getDatabasesEndpoint(`reports/post-attestation/export/?campaign_id=${selectedCampaignId}`),
        '_blank'
      );
    } catch (err) {
      setError('Error exporting CSV: ' + err.message);
    }
  };

  const exportToPDF = () => {
    if (!reportData || !reportData.attestation_records) {
      setError('Please generate the report first before exporting');
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      setError('Please allow popups to export PDF');
      return;
    }

    const timestamp = formatDateTimeForReport(new Date().toISOString());
    const scopeCriteria = reportData.executive_summary.scope_criteria;
    const stats = reportData.statistics;

    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Post-Attestation Report - ${reportData.executive_summary.campaign_name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; font-size: 10px; }
          h1 { color: #2c3e50; margin-bottom: 20px; font-size: 20px; }
          h2 { color: #34495e; margin-top: 20px; font-size: 16px; border-bottom: 2px solid #3498db; padding-bottom: 5px; }
          h3 { color: #34495e; margin-top: 15px; font-size: 14px; }
          .summary-grid { display: grid; grid-template-columns: 1fr 1fr 1fr 1fr; gap: 10px; margin-bottom: 20px; }
          .summary-item { background: #f8f9fa; padding: 10px; border-radius: 5px; }
          .summary-item strong { display: block; color: #2c3e50; margin-bottom: 3px; }
          .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 20px; }
          .stat-card { background: #f8f9fa; padding: 15px; border-radius: 5px; text-align: center; }
          .stat-card .number { font-size: 24px; font-weight: bold; }
          .stat-card .label { font-size: 11px; color: #666; }
          .text-success { color: #27ae60; font-weight: bold; }
          .text-warning { color: #f39c12; font-weight: bold; }
          .text-danger { color: #e74c3c; font-weight: bold; }
          .text-info { color: #3498db; }
          .text-primary { color: #2980b9; }
          .text-secondary { color: #95a5a6; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 8px; }
          th { background: #2c3e50; color: white; padding: 5px 3px; text-align: left; font-size: 8px; }
          td { border: 1px solid #ddd; padding: 3px; vertical-align: top; }
          tr:nth-child(even) { background: #f9f9f9; }
          .verified { color: #27ae60; font-weight: bold; }
          .not-implemented { color: #e74c3c; font-weight: bold; }
          .error { color: #95a5a6; font-weight: bold; }
          .footer { margin-top: 30px; text-align: center; color: #7f8c8d; font-size: 10px; border-top: 1px solid #ddd; padding-top: 10px; }
          @media print {
            body { margin: 10px; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            thead { display: table-header-group; }
          }
        </style>
      </head>
      <body>
        <h1>Post-Attestation Report</h1>

        <h2>Executive Summary</h2>
        <div style="display: flex; gap: 20px; margin-bottom: 15px;">
          <div style="flex: 1; border-right: 1px solid #ddd; padding-right: 20px;">
            <h3 style="font-size: 14px; color: #3498db; margin-bottom: 10px;">Campaign Details</h3>
            <p><strong>Campaign Name:</strong> ${reportData.executive_summary.campaign_name}</p>
            <p><strong>Compliance Framework:</strong> ${reportData.executive_summary.compliance_framework || 'N/A'}</p>
            <p><strong>Database Type:</strong> ${reportData.executive_summary.database_type || 'N/A'}</p>
            <p><strong>Start Date:</strong> ${reportData.executive_summary.start_date ? formatDateTimeForReport(reportData.executive_summary.start_date) : 'N/A'}</p>
            <p><strong>End Date:</strong> ${reportData.executive_summary.end_date ? formatDateTimeForReport(reportData.executive_summary.end_date) : 'N/A'}</p>
          </div>
          <div style="flex: 1; padding-left: 20px;">
            <h3 style="font-size: 14px; color: #3498db; margin-bottom: 10px;">Scope Details</h3>
            <p><strong>Total Accounts:</strong> ${stats.total_accounts || stats.total_in_scope}</p>
            <p><strong>Accounts in Scope:</strong> ${stats.total_in_scope}</p>
            <p><strong>Accounts Included:</strong> <span class="${scopeCriteria?.active_only ? 'text-warning' : 'text-info'}">${scopeCriteria?.active_only ? 'Active Only' : 'All'}</span></p>
            <p><strong>Privilege Levels:</strong> ${scopeCriteria?.privilege_levels && scopeCriteria.privilege_levels.length > 0
              ? scopeCriteria.privilege_levels.map(level => `<span class="text-primary">${level}</span>`).join(', ')
              : '<span class="text-info">All</span>'
            }</p>
          </div>
        </div>

        <h3>Attestation Decisions</h3>
        <div class="stats-grid">
          <div class="stat-card">
            <div class="number" style="color: #2980b9;">${stats.total_in_scope}</div>
            <div class="label">Total In Scope</div>
          </div>
          <div class="stat-card">
            <div class="number" style="color: #27ae60;">${stats.retained}</div>
            <div class="label">Retained</div>
          </div>
          <div class="stat-card">
            <div class="number" style="color: #f39c12;">${stats.modified}</div>
            <div class="label">Modify</div>
          </div>
          <div class="stat-card">
            <div class="number" style="color: #e74c3c;">${stats.removed}</div>
            <div class="label">Removed</div>
          </div>
        </div>

        <h3>Implementation Verification</h3>
        <div class="stats-grid" style="grid-template-columns: repeat(3, 1fr);">
          <div class="stat-card">
            <div class="number" style="color: #27ae60;">${stats.implemented}</div>
            <div class="label">Verified</div>
          </div>
          <div class="stat-card">
            <div class="number" style="color: #e74c3c;">${stats.modify_not_implemented || 0}</div>
            <div class="label">Modify Pending</div>
          </div>
          <div class="stat-card">
            <div class="number" style="color: #e74c3c;">${stats.remove_not_implemented || 0}</div>
            <div class="label">Remove Pending</div>
          </div>
        </div>


        <h2>Attestation Results with Implementation Verification</h2>
        <table>
          <thead>
            <tr>
              <th>Database</th>
              <th>Username</th>
              <th>Employee</th>
              <th>Priv Level (Attestation)</th>
              <th>Attester</th>
              <th>Decision</th>
              <th>Target Priv</th>
              <th>Action Required</th>
              <th>Attestation Date</th>
              <th>Current Status</th>
              <th>Current Priv</th>
              <th>Implementation</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            ${reportData.attestation_records.map(record => `
              <tr>
                <td>${record.database_name || ''}</td>
                <td>${record.db_username || ''}</td>
                <td>${record.user_display_name || ''}<br/><small style="word-break: break-all;">${record.user_email || ''}</small></td>
                <td>${record.privilege_level || ''}</td>
                <td>${record.manager_name || ''}<br/><small style="word-break: break-all;">${record.attester_email || ''}</small></td>
                <td><span class="${
                  record.attestation_decision === 'Retained' ? 'text-success' :
                  record.attestation_decision === 'Modify' ? 'text-warning' :
                  record.attestation_decision === 'Removed' ? 'text-danger' : 'text-secondary'
                }">${record.attestation_decision || 'Pending'}</span></td>
                <td>${record.target_privilege_level || '-'}</td>
                <td><span class="${
                  record.attestation_decision === 'Removed' ? 'text-danger' :
                  record.attestation_decision === 'Modify' ? 'text-warning' :
                  record.attestation_decision === 'Retained' ? 'text-success' : 'text-secondary'
                }" style="font-size: 7px;">${record.required_action || '-'}</span></td>
                <td>${record.attestation_date ? formatDateTimeForReport(record.attestation_date) : '-'}</td>
                <td>${record.current_account_status || 'N/A'}</td>
                <td>${record.current_privilege_level || 'N/A'}</td>
                <td class="${
                  record.implementation_status === 'VERIFIED' ? 'verified' :
                  record.implementation_status === 'NOT_IMPLEMENTED' ? 'not-implemented' :
                  record.implementation_status === 'ERROR' ? 'error' : ''
                }">${record.implementation_status || '-'}</td>
                <td style="font-size: 7px;">${record.implementation_details || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <p>Report Generated: ${timestamp}</p>
          <p>Post-Attestation Report - Database Access Management & Attestation</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  };

  const getFilteredRecords = () => {
    if (!reportData || !reportData.attestation_records) return [];

    return reportData.attestation_records.filter(record => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        record.database_name?.toLowerCase().includes(searchLower) ||
        record.db_username?.toLowerCase().includes(searchLower) ||
        record.user_display_name?.toLowerCase().includes(searchLower) ||
        record.user_email?.toLowerCase().includes(searchLower) ||
        record.attester_email?.toLowerCase().includes(searchLower);

      const matchesDecision =
        filterDecision === 'all' ||
        record.attestation_decision === filterDecision ||
        (filterDecision === 'Pending' && !record.attestation_decision);

      const matchesImplementation =
        filterImplementation === 'all' ||
        record.implementation_status === filterImplementation;

      return matchesSearch && matchesDecision && matchesImplementation;
    });
  };

  const getPaginatedRecords = () => {
    const filtered = getFilteredRecords();
    const startIndex = (currentPage - 1) * recordsPerPage;
    return filtered.slice(startIndex, startIndex + recordsPerPage);
  };

  const getTotalPages = () => {
    return Math.ceil(getFilteredRecords().length / recordsPerPage);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterDecision, filterImplementation]);

  // Use formatDateTime from dateUtils for consistent formatting with timezone

  const getDecisionTextClass = (decision) => {
    switch (decision) {
      case 'Retained': return 'text-success';
      case 'Modify': return 'text-warning';
      case 'Removed': return 'text-danger';
      default: return 'text-secondary';
    }
  };

  const getImplementationTextClass = (status) => {
    switch (status) {
      case 'VERIFIED': return 'text-success';
      case 'NOT_IMPLEMENTED': return 'text-danger';
      case 'ERROR': return 'text-dark';
      case 'PENDING': return 'text-secondary';
      default: return 'text-muted';
    }
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <h2 className="text-primary mb-4">
            <i className="fas fa-file-check me-2"></i>
            Post-Attestation Report
          </h2>

          <div className="card shadow-sm mb-4">
            <div className="card-header bg-primary text-white">
              <h5 className="mb-0">
                <i className="fas fa-filter me-2"></i>
                Report Selection
              </h5>
            </div>
            <div className="card-body">
              <div className="row align-items-end">
                <div className="col-md-6">
                  <label className="form-label fw-bold">Select Completed Campaign</label>
                  {isLoadingCampaigns ? (
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  ) : (
                    <select
                      className="form-select"
                      value={selectedCampaignId}
                      onChange={(e) => setSelectedCampaignId(e.target.value)}
                    >
                      <option value="">-- Select a Completed Campaign --</option>
                      {campaigns.map(campaign => (
                        <option key={campaign.campaign_id} value={campaign.campaign_id}>
                          {campaign.campaign_name} ({campaign.compliance_framework})
                        </option>
                      ))}
                    </select>
                  )}
                  {campaigns.length === 0 && !isLoadingCampaigns && (
                    <small className="text-muted">No completed campaigns available</small>
                  )}
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-primary w-100"
                    onClick={loadReport}
                    disabled={isLoading || !selectedCampaignId}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                        Loading
                      </>
                    ) : (
                      <>
                        <i className="fas fa-search me-1"></i>
                        Generate
                      </>
                    )}
                  </button>
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-success w-100"
                    onClick={exportToCSV}
                    disabled={!selectedCampaignId || !reportData}
                  >
                    <i className="fas fa-file-csv me-1"></i>
                    CSV
                  </button>
                </div>
                <div className="col-md-2">
                  <button
                    className="btn btn-danger w-100"
                    onClick={exportToPDF}
                    disabled={!selectedCampaignId || !reportData}
                  >
                    <i className="fas fa-file-pdf me-1"></i>
                    PDF
                  </button>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger alert-dismissible fade show">
              <i className="fas fa-exclamation-circle me-2"></i>
              {error}
              <button type="button" className="btn-close" onClick={() => setError(null)}></button>
            </div>
          )}

          {reportData && (
            <>
              {/* Executive Summary */}
              <div className="card shadow-sm mb-4">
                <div className="card-header bg-info text-white">
                  <h5 className="mb-0">
                    <i className="fas fa-clipboard-list me-2"></i>
                    Executive Summary
                  </h5>
                </div>
                <div className="card-body">
                  {/* Campaign Details & Scope - Split Screen */}
                  <div className="row">
                    {/* Campaign Details */}
                    <div className="col-md-6 border-end">
                      <h6 className="text-info mb-2">
                        <i className="fas fa-info-circle me-2"></i>
                        Campaign Details
                      </h6>
                      <div className="row">
                        <div className="col-6">
                          <p className="mb-1"><strong>Campaign Name:</strong> {reportData.executive_summary.campaign_name}</p>
                          <p className="mb-1"><strong>Compliance Framework:</strong> {reportData.executive_summary.compliance_framework || 'N/A'}</p>
                          <p className="mb-1"><strong>Database Type:</strong> {reportData.executive_summary.database_type || 'N/A'}</p>
                        </div>
                        <div className="col-6">
                          <p className="mb-1"><strong>Start Date:</strong> {formatDateTime(reportData.executive_summary.start_date)}</p>
                          <p className="mb-1"><strong>End Date:</strong> {formatDateTime(reportData.executive_summary.end_date)}</p>
                          <p className="mb-1"><strong>Report Generated:</strong> {formatDateTime(reportData.executive_summary.report_generated_at)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Scope Details */}
                    <div className="col-md-6">
                      <h6 className="text-info mb-2">
                        <i className="fas fa-filter me-2"></i>
                        Scope Details
                      </h6>
                      <div className="row">
                        <div className="col-6">
                          <p className="mb-1"><strong>Total Accounts:</strong> <span className="text-secondary fw-bold">{reportData.statistics.total_accounts || reportData.statistics.total_in_scope}</span></p>
                          <p className="mb-1"><strong>Accounts in Scope:</strong> <span className="text-primary fw-bold">{reportData.statistics.total_in_scope}</span></p>
                        </div>
                        <div className="col-6">
                          <p className="mb-1"><strong>Accounts Included:</strong>{' '}
                            <span className={reportData.executive_summary.scope_criteria?.active_only ? 'text-warning' : 'text-info'}>
                              {reportData.executive_summary.scope_criteria?.active_only ? 'Active Only' : 'All'}
                            </span>
                          </p>
                          <p className="mb-1"><strong>Privilege Levels:</strong>{' '}
                            {reportData.executive_summary.scope_criteria?.privilege_levels && reportData.executive_summary.scope_criteria.privilege_levels.length > 0 ? (
                              reportData.executive_summary.scope_criteria.privilege_levels.map((level, idx) => (
                                <span key={idx} className="text-primary me-1">{level}{idx < reportData.executive_summary.scope_criteria.privilege_levels.length - 1 ? ',' : ''}</span>
                              ))
                            ) : (
                              <span className="text-info">All</span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Attestation Decisions & Implementation Verification - Side by Side */}
                  <div className="mt-3 pt-3 border-top">
                    <div className="row">
                      {/* Attestation Decisions */}
                      <div className="col-md-6">
                        <h6 className="text-info mb-2">
                          <i className="fas fa-chart-pie me-2"></i>
                          Attestation Decisions
                          <small className="text-muted ms-2" style={{fontSize: '0.75rem'}}>(Click to filter)</small>
                        </h6>
                        <div className="d-flex gap-4">
                          <span
                            style={{cursor: 'pointer'}}
                            onClick={() => { setFilterDecision('Retained'); setFilterImplementation('all'); }}
                            title="Click to filter by Retained"
                          >
                            <i className="fas fa-check-circle text-success me-1"></i>
                            <strong className="text-success">{reportData.statistics.retained}</strong>
                            <span className="text-muted ms-1">Retained</span>
                          </span>
                          <span
                            style={{cursor: 'pointer'}}
                            onClick={() => { setFilterDecision('Modify'); setFilterImplementation('all'); }}
                            title="Click to filter by Modify"
                          >
                            <i className="fas fa-edit text-warning me-1"></i>
                            <strong className="text-warning">{reportData.statistics.modified}</strong>
                            <span className="text-muted ms-1">Modify</span>
                          </span>
                          <span
                            style={{cursor: 'pointer'}}
                            onClick={() => { setFilterDecision('Removed'); setFilterImplementation('all'); }}
                            title="Click to filter by Removed"
                          >
                            <i className="fas fa-times-circle text-danger me-1"></i>
                            <strong className="text-danger">{reportData.statistics.removed}</strong>
                            <span className="text-muted ms-1">Removed</span>
                          </span>
                        </div>
                      </div>

                      {/* Implementation Verification */}
                      <div className="col-md-6">
                        <h6 className="text-info mb-2">
                          <i className="fas fa-check-double me-2"></i>
                          Implementation Verification
                          <small className="text-muted ms-2" style={{fontSize: '0.75rem'}}>(Click to filter)</small>
                        </h6>
                        <div className="d-flex gap-4">
                          <span
                            style={{cursor: 'pointer'}}
                            onClick={() => { setFilterImplementation('VERIFIED'); setFilterDecision('all'); }}
                            title="Click to filter by Verified"
                          >
                            <i className="fas fa-check-double text-success me-1"></i>
                            <strong className="text-success">{reportData.statistics.implemented}</strong>
                            <span className="text-muted ms-1">Verified</span>
                          </span>
                          <span
                            style={{cursor: 'pointer'}}
                            onClick={() => { setFilterImplementation('NOT_IMPLEMENTED'); setFilterDecision('Modify'); }}
                            title="Click to filter by Modify Pending"
                          >
                            <i className="fas fa-clock text-warning me-1"></i>
                            <strong className="text-warning">{reportData.statistics.modify_not_implemented || 0}</strong>
                            <span className="text-muted ms-1">Modify Pending</span>
                          </span>
                          <span
                            style={{cursor: 'pointer'}}
                            onClick={() => { setFilterImplementation('NOT_IMPLEMENTED'); setFilterDecision('Removed'); }}
                            title="Click to filter by Remove Pending"
                          >
                            <i className="fas fa-exclamation-circle text-danger me-1"></i>
                            <strong className="text-danger">{reportData.statistics.remove_not_implemented || 0}</strong>
                            <span className="text-muted ms-1">Remove Pending</span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* Database Topology Section */}
              {reportData.topology && reportData.topology.length > 0 && (
                <TopologySection topology={reportData.topology} />
              )}

              {/* Attestation Records Table */}
              <div className="card shadow-sm">
                <div className="card-header bg-dark text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      <i className="fas fa-table me-2"></i>
                      Attestation Results with Implementation Verification
                    </h5>
                    <span className="text-light">
                      {getFilteredRecords().length} records
                    </span>
                  </div>
                </div>
                <div className="card-body">
                  {/* Filters */}
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by database, username, employee, attester..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="col-md-2">
                      <select
                        className="form-select"
                        value={filterDecision}
                        onChange={(e) => setFilterDecision(e.target.value)}
                      >
                        <option value="all">All Decisions</option>
                        <option value="Retained">Retained</option>
                        <option value="Modify">Modify</option>
                        <option value="Removed">Removed</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <select
                        className="form-select"
                        value={filterImplementation}
                        onChange={(e) => setFilterImplementation(e.target.value)}
                      >
                        <option value="all">All Implementation Status</option>
                        <option value="VERIFIED">Verified Implemented</option>
                        <option value="NOT_IMPLEMENTED">Not Implemented</option>
                        <option value="ERROR">Verification Error</option>
                      </select>
                    </div>
                    <div className="col-md-1">
                      <select
                        className="form-select"
                        value={recordsPerPage}
                        onChange={(e) => {
                          setRecordsPerPage(parseInt(e.target.value));
                          setCurrentPage(1);
                        }}
                      >
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                      </select>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <table className="table table-sm table-bordered table-striped table-hover">
                      <thead className="table-dark sticky-top">
                        <tr>
                          <th>Database</th>
                          <th>Username</th>
                          <th>Employee</th>
                          <th>Priv Level (Attestation)</th>
                          <th>Attester</th>
                          <th>Decision</th>
                          <th>Target Priv</th>
                          <th>Action Required</th>
                          <th>Attestation Date</th>
                          <th>Comments</th>
                          <th>IP Address</th>
                          <th>Current Status</th>
                          <th>Current Priv</th>
                          <th>Implementation</th>
                          <th>Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedRecords().map((record, index) => (
                          <tr key={record.report_id || index}>
                            <td>{record.database_name}</td>
                            <td><code>{record.db_username}</code></td>
                            <td style={{maxWidth: '150px'}}>
                              {record.user_display_name && record.user_display_name !== 'Unknown User' ? (
                                <>
                                  {record.user_display_name}
                                  <br/>
                                  <small className="text-muted" style={{wordBreak: 'break-all'}}>{record.user_email || ''}</small>
                                </>
                              ) : (
                                record.is_system_account || record.attester_type === 'Database Owner' ? (
                                  <span className="text-secondary">
                                    <i className="fas fa-cog me-1"></i>System Account
                                  </span>
                                ) : record.attester_type === 'Account Owner' ? (
                                  <span className="text-info">
                                    <i className="fas fa-robot me-1"></i>Service Account
                                  </span>
                                ) : (
                                  <span className="text-muted">-</span>
                                )
                              )}
                            </td>
                            <td>
                              <span className="text-info">{record.privilege_level || 'N/A'}</span>
                            </td>
                            <td style={{maxWidth: '150px'}}>
                              {record.manager_name || 'N/A'}
                              <br/>
                              <small className="text-muted" style={{wordBreak: 'break-all'}}>{record.attester_email || ''}</small>
                              <br/>
                              <small className="text-secondary">{record.attester_type || ''}</small>
                            </td>
                            <td>
                              <span className={`fw-bold ${getDecisionTextClass(record.attestation_decision)}`}>
                                {record.attestation_decision || 'Pending'}
                              </span>
                            </td>
                            <td>{record.target_privilege_level || '-'}</td>
                            <td style={{maxWidth: '200px'}}>
                              {record.required_action ? (
                                <small className={
                                  record.attestation_decision === 'Removed' ? 'text-danger' :
                                  record.attestation_decision === 'Modify' ? 'text-warning' :
                                  record.attestation_decision === 'Retained' ? 'text-success' : 'text-muted'
                                }>
                                  {record.required_action}
                                </small>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                            <td>
                              <small>{formatDateTime(record.attestation_date)}</small>
                            </td>
                            <td>
                              <small>{record.attestation_comments || '-'}</small>
                            </td>
                            <td>
                              <small>{record.attestation_ip_address || '-'}</small>
                            </td>
                            <td>
                              <span className={
                                record.current_account_status === 'OPEN' ? 'text-success' :
                                record.current_account_status === 'NOT_FOUND' ? 'text-danger' : 'text-secondary'
                              }>
                                {record.current_account_status || 'N/A'}
                              </span>
                            </td>
                            <td>{record.current_privilege_level || 'N/A'}</td>
                            <td>
                              <span className={`fw-bold ${getImplementationTextClass(record.implementation_status)}`}>
                                {record.implementation_status || '-'}
                              </span>
                            </td>
                            <td>
                              <small title={record.implementation_details}>
                                {record.implementation_details?.length > 50
                                  ? record.implementation_details.substring(0, 50) + '...'
                                  : record.implementation_details || '-'}
                              </small>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {getTotalPages() > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted">
                        Showing {((currentPage - 1) * recordsPerPage) + 1} to{' '}
                        {Math.min(currentPage * recordsPerPage, getFilteredRecords().length)} of{' '}
                        {getFilteredRecords().length} records
                      </div>
                      <nav>
                        <ul className="pagination mb-0">
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(1)}
                              disabled={currentPage === 1}
                            >
                              <i className="fas fa-angle-double-left"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage - 1)}
                              disabled={currentPage === 1}
                            >
                              <i className="fas fa-angle-left"></i>
                            </button>
                          </li>
                          <li className="page-item active">
                            <span className="page-link">
                              Page {currentPage} of {getTotalPages()}
                            </span>
                          </li>
                          <li className={`page-item ${currentPage === getTotalPages() ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(currentPage + 1)}
                              disabled={currentPage === getTotalPages()}
                            >
                              <i className="fas fa-angle-right"></i>
                            </button>
                          </li>
                          <li className={`page-item ${currentPage === getTotalPages() ? 'disabled' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(getTotalPages())}
                              disabled={currentPage === getTotalPages()}
                            >
                              <i className="fas fa-angle-double-right"></i>
                            </button>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {!reportData && !isLoading && !error && (
            <div className="alert alert-info">
              <i className="fas fa-info-circle me-2"></i>
              Select a completed campaign and click "Generate Report" to view attestation results and implementation verification.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostAttestationReport;
